import { agentsApi, byUrgency, pollWhile, talkProject } from '../agents-api.ts';
import type { AgentInfo, ProjectInfo } from '../agents-api.ts';
import { button, emptyState, linkButton, pageHeader, section } from '../components.ts';
import { icon } from '../icons.ts';
import { h } from '../ui.ts';
import { newAgentDialog } from './agents/new-agent.ts';
import { agentRow, isEditing } from './agents/row.ts';
import { linkedBadge } from './projects.ts';

/** One project: its agents (polled), a button to talk about it, and a new agent (own projects only). */
export async function projectPage(slug: string): Promise<HTMLElement> {
  const p = await agentsApi.project(slug);
  const rows = h('div', { class: 'agent-rows' });
  const draw = (agents: AgentInfo[]) => drawRows(rows, agents, () => void refresh());
  const refresh = async () => draw((await agentsApi.project(slug)).agents);
  const root = h(
    'div',
    { class: 'page' },
    h('a', { class: 'back', href: '#/projects' }, icon.back(14), 'Projects'),
    pageHeader(
      h('span', { class: 'row' }, p.name, p.linearOrg ? linkedBadge() : null),
      sub(p),
      p.linearOrg
        ? null
        : button([icon.plus(), 'New agent'], () => newAgentDialog([p], p.slug), { variant: 'outline' }),
      button(
        [icon.mic(16), 'Talk about this project'],
        () => {
          talkProject(p.slug);
          location.hash = '#/talk';
        },
        { extra: 'glow' },
      ),
    ),
    p.linearOrg
      ? h(
          'div',
          { class: 'card accent' },
          h('span', { class: 'card-title' }, 'Built by Atlas through Linear'),
          h(
            'p',
            { class: 'muted small' },
            `Work on ${p.name} is filed as issues in the ${p.linearOrg} Linear team; Atlas builds them. Talk about it and she files the issues.`,
          ),
        )
      : null,
    section('Agents', rows),
  );
  draw(p.agents);
  pollWhile(root, 3000, refresh);
  return root;
}

function sub(p: ProjectInfo): Array<Node | string> {
  const out: Array<Node | string> = [p.description || 'No description.'];
  if (p.repository)
    out.push(
      h('br'),
      linkButton([icon.branch(12), p.repository, icon.open(12)], `https://github.com/${p.repository}`, 'ghost', 'sm'),
      h('span', { class: 'mono muted' }, ` base ${p.baseBranch}`),
    );
  return out;
}

function drawRows(host: HTMLElement, agents: AgentInfo[], changed: () => void) {
  if (!agents.length) {
    host.replaceChildren(emptyState('No agents on this project', 'Start one here, or ask her in a talk.'));
    return;
  }
  const next = [...agents].sort(byUrgency).map((a) => {
    const old = host.querySelector<HTMLElement>(`:scope > [data-id="${CSS.escape(a.id)}"]`);
    if (old && (old.dataset.v === String(a.updatedAt) || isEditing(old))) return old;
    return agentRow(a, { project: false, onChange: changed });
  });
  if (next.length !== host.children.length || next.some((el, i) => host.children[i] !== el))
    host.replaceChildren(...next);
}
